// Web Push payload for incoming-call ringing.
//
// Web Push (VAPID + push API) is the browser analogue of iOS VoIP pushes.
// A hostile server can submit any payload, so the payload MUST NOT contain
// plaintext caller info or a routable descriptor: it carries only an opaque
// `callId` (ULID), the `callerUserId` (matched against the local known-peers
// set), the kind of call, and a `pushedAt` timestamp. The encrypted call
// descriptor rides the existing E2EE channel — out of scope for this file.
//
// Wire-format: the push body is a JSON object. Our app-specific fields live
// under the `"kaether"` sub-object, identical to the iOS VoIP push format so
// a single server-side push-notification path serves both platforms.
//
// This file is pure parsing + value types. No Service Worker registration,
// no Notification API, no network. Failure modes are typed errors so the
// caller can log + synthesise the "Unknown caller" notification fallback.
//
// iOS counterpart: kaether_for_ios/Stoat/E2EE/CallRingingPayload.swift

/** Whether the call is audio-only or includes video. */
export type CallKind = "audio" | "video";

/**
 * Decoded form of the inner "kaether" sub-object of a Web Push payload.
 * The parser below extracts and validates the fields without trusting the
 * server.
 */
export interface CallRingingPushPayload {
  readonly schema: number;
  readonly callId: string;
  readonly callerUserId: string;
  readonly pushedAt: number; // unix seconds (Int64 on iOS, number on web — safe up to 2^53)
  readonly kind: CallKind;
}

// ---------------------------------------------------------------------------
// Errors
// ---------------------------------------------------------------------------

export type CallRingingPushErrorKind =
  | { kind: "malformedPushPayload"; reason: string }
  | { kind: "unsupportedSchema"; schema: number }
  | { kind: "emptyCallId" }
  | { kind: "emptyCallerId" }
  | { kind: "futureTimestamp" };

export class CallRingingPushError extends Error {
  constructor(public readonly detail: CallRingingPushErrorKind) {
    super(CallRingingPushError.message(detail));
    this.name = "CallRingingPushError";
  }

  private static message(d: CallRingingPushErrorKind): string {
    switch (d.kind) {
      case "malformedPushPayload":
        return `malformed push payload: ${d.reason}`;
      case "unsupportedSchema":
        return `unsupported schema: ${d.schema}`;
      case "emptyCallId":
        return "empty callId";
      case "emptyCallerId":
        return "empty callerUserId";
      case "futureTimestamp":
        return "future timestamp";
    }
  }
}

// ---------------------------------------------------------------------------
// Parser
// ---------------------------------------------------------------------------

export const CallRingingPushParser = {
  /** Outer key inside the push body that wraps our app-specific fields. */
  payloadKey: "kaether" as const,

  /** Current schema version. */
  currentSchema: 1 as const,

  /**
   * Maximum forward clock skew (seconds) tolerated on `pushedAt`.
   * Anything claiming to be from the far future is rejected — a legitimate
   * Web Push will never be more than a minute ahead, and a large skew
   * implies a forged push origin. Mirrors iOS `maxFutureSkewSeconds = 60`.
   */
  maxFutureSkewSeconds: 60 as const,

  /**
   * Maximum byte size of the outer push body we will parse. Web Push
   * payloads are limited to 4096 bytes by spec; we add a conservative bound
   * here so a rogue server cannot cause unlimited JSON parsing work.
   */
  maxPayloadBytes: 4096 as const,

  /**
   * Parse and validate the push payload object. `rawBody` should be the
   * already-parsed outer JSON object (caller owns JSON.parse). Returns the
   * decoded value type or throws `CallRingingPushError`.
   *
   * `now` defaults to `Math.floor(Date.now() / 1000)` (unix seconds) and
   * is injectable for deterministic tests.
   */
  parse(
    rawBody: unknown,
    now: number = Math.floor(Date.now() / 1000),
  ): CallRingingPushPayload {
    if (rawBody === null || typeof rawBody !== "object") {
      throw new CallRingingPushError({
        kind: "malformedPushPayload",
        reason: "push body is not an object",
      });
    }
    const body = rawBody as Record<string, unknown>;
    const inner = body[CallRingingPushParser.payloadKey];
    if (inner === undefined || inner === null || typeof inner !== "object") {
      throw new CallRingingPushError({
        kind: "malformedPushPayload",
        reason: `missing '${CallRingingPushParser.payloadKey}' sub-object`,
      });
    }
    const obj = inner as Record<string, unknown>;

    // schema
    if (typeof obj["schema"] !== "number") {
      throw new CallRingingPushError({
        kind: "malformedPushPayload",
        reason: "missing schema",
      });
    }
    const schema = obj["schema"] as number;
    if (schema !== CallRingingPushParser.currentSchema) {
      throw new CallRingingPushError({ kind: "unsupportedSchema", schema });
    }

    // callId
    if (typeof obj["callId"] !== "string") {
      throw new CallRingingPushError({
        kind: "malformedPushPayload",
        reason: "missing callId",
      });
    }
    const callId = obj["callId"] as string;
    if (callId.length === 0) {
      throw new CallRingingPushError({ kind: "emptyCallId" });
    }

    // callerUserId
    if (typeof obj["callerUserId"] !== "string") {
      throw new CallRingingPushError({
        kind: "malformedPushPayload",
        reason: "missing callerUserId",
      });
    }
    const callerUserId = obj["callerUserId"] as string;
    if (callerUserId.length === 0) {
      throw new CallRingingPushError({ kind: "emptyCallerId" });
    }

    // pushedAt
    if (typeof obj["pushedAt"] !== "number") {
      throw new CallRingingPushError({
        kind: "malformedPushPayload",
        reason: "missing pushedAt",
      });
    }
    const pushedAt = obj["pushedAt"] as number;
    if (pushedAt > now + CallRingingPushParser.maxFutureSkewSeconds) {
      throw new CallRingingPushError({ kind: "futureTimestamp" });
    }

    // kind
    if (typeof obj["kind"] !== "string") {
      throw new CallRingingPushError({
        kind: "malformedPushPayload",
        reason: "missing kind",
      });
    }
    const kindRaw = obj["kind"] as string;
    if (kindRaw !== "audio" && kindRaw !== "video") {
      throw new CallRingingPushError({
        kind: "malformedPushPayload",
        reason: `unknown kind: ${kindRaw}`,
      });
    }
    const kind: CallKind = kindRaw;

    return { schema, callId, callerUserId, pushedAt, kind };
  },
} as const;
